import React, { useEffect } from "react";

import { Box, Typography, Paper } from "@mui/material";
import {AuthenticatedTemplate, UnauthenticatedTemplate} from "@azure/msal-react";

import LoginButton from "../components/auth/LoginButton";
import ParticleNetworkBackground from "../components/auth/ParticleNetworkBackground";
import uiConfig from "../config/uiConfig";
import aaysLogo from "../assets/img/Aays.png";
import { useNavigate } from "react-router-dom";
import { Loader } from "lucide-react";
import { useUserRole } from "../context/UserRoleContext";
import { getDefaultRouteForRole } from "../auth/roles";

function Login() {
  const navigate = useNavigate();
  const { role, loading: roleLoading } = useUserRole();

   const RedirectToDashboard = () => {
    useEffect(() => {
      if (roleLoading) {
        return;
      }
      navigate(getDefaultRouteForRole(role));
    }, [roleLoading, role]);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader size={40} className="animate-spin text-blue-600" />
      </div>
    );
  };


  return (
    <>
    <AuthenticatedTemplate>
      <RedirectToDashboard />
    </AuthenticatedTemplate>
    <UnauthenticatedTemplate>

          <Box
            sx={{
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
              p: 2,
              background: `linear-gradient(135deg, ${uiConfig.sidebarBackgroundColor}, ${uiConfig.sidebarBackgroundColorSecondary}, ${uiConfig.sidebarBackgroundColor})`,
              backgroundSize: "200% 200%",
              animation: "gradientShift 12s ease infinite",
              "@keyframes gradientShift": {
                "0%": { backgroundPosition: "0% 50%" },
                "50%": { backgroundPosition: "100% 50%" },
                "100%": { backgroundPosition: "0% 50%" },
              },
              "@keyframes floatSlow": {
                "0%, 100%": { transform: "translate(0, 0) scale(1)" },
                "50%": { transform: "translate(20px, -30px) scale(1.05)" },
              },
              "@keyframes floatSlower": {
                "0%, 100%": { transform: "translate(0, 0) scale(1)" },
                "50%": { transform: "translate(-25px, 25px) scale(1.08)" },
              },
              "@keyframes fadeUp": {
                from: { opacity: 0, transform: "translateY(16px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
              "@keyframes glowPulse": {
                "0%, 100%": { boxShadow: "0 0 0 rgba(255,255,255,0)" },
                "50%": { boxShadow: "0 0 40px rgba(255,255,255,0.15)" },
              },
            }}
          >
            {/* Decorative dot-grid texture */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                opacity: 0.25,
                backgroundImage: "radial-gradient(rgba(255,255,255,0.35) 1px, transparent 1px)",
                backgroundSize: "26px 26px",
              }}
            />

            <ParticleNetworkBackground particleCount={180} linkDistance={150} />

            {/* Floating blurred color orbs */}
            <Box sx={{ position: "absolute", top: -140, left: -100, width: 340, height: 340, borderRadius: "50%", background: "radial-gradient(circle, #f59e0b55, transparent 70%)", filter: "blur(10px)", animation: "floatSlow 9s ease-in-out infinite" }} />
            <Box sx={{ position: "absolute", top: -80, right: -60, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, #0ea5e955, transparent 70%)", filter: "blur(10px)", animation: "floatSlower 11s ease-in-out infinite" }} />
            <Box sx={{ position: "absolute", bottom: -160, left: "15%", width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, #10b98150, transparent 70%)", filter: "blur(10px)", animation: "floatSlow 13s ease-in-out infinite" }} />
            <Box sx={{ position: "absolute", bottom: -100, right: "10%", width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(circle, #f43f5e50, transparent 70%)", filter: "blur(10px)", animation: "floatSlower 10s ease-in-out infinite" }} />

            {/* Thin decorative ring outlines for depth */}
            <Box sx={{ position: "absolute", top: -120, left: -100, width: 320, height: 320, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.25)" }} />
            <Box sx={{ position: "absolute", top: -60, right: 80, width: 220, height: 220, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.2)" }} />
            <Box sx={{ position: "absolute", bottom: -140, left: "20%", width: 300, height: 300, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)" }} />

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 460,
                p: { xs: 3.5, sm: 5 },
                borderRadius: 4,
                textAlign: "center",
                position: "relative",
                zIndex: 1,
                background: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255,255,255,0.35)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
                animation: "fadeUp 0.7s ease-out, glowPulse 5s ease-in-out infinite 0.7s",
              }}
            >
              <Box
                component="img"
                src={aaysLogo}
                alt="Aays"
                sx={{
                  height: 34,
                  width: "auto",
                  mb: 3,
                  filter: "drop-shadow(0 0 14px rgba(255,255,255,0.45))",
                }}
              />

              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 3,
                  color: "rgba(255,255,255,0.85)",
                  mb: 1.5,
                }}
              >
                IT SERVICE DESK PORTAL
              </Typography>

              <Typography
                variant="h4"
                fontWeight={800}
                mb={1.5}
                sx={{
                  background: "linear-gradient(90deg, #ffffff, #f5d0fe, #ffffff)",
                  backgroundSize: "200% auto",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: "gradientShift 6s ease infinite",
                }}
              >
                Welcome back!
              </Typography>

              <Typography sx={{ color: "rgba(255,255,255,0.85)", mb: 4 }}>
                Sign in to access the Portal and continue.
              </Typography>

              <LoginButton />

              <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: 12.5, mt: 3 }}>
                Access to this system is limited. Contact{" "}
                <Box
                  component="a"
                  href="mailto:anshuman.bhar@aaysanalytics.com"
                  sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600, textDecoration: "underline" }}
                >
                  anshuman.bhar@aaysanalytics.com
                </Box>{" "}
                for account enablement and onboarding support.
              </Typography>
            </Paper>
          </Box>

    </UnauthenticatedTemplate>
    </>
  );
}

export default Login;
